import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../../styles/HowItWorks.css";

// Renamed from About. Nothing on this page was ever *about* WallBurst -- no
// company, no team, no mission -- it is a two-sided explainer, and the nav link
// pointing at it already read "How it works". The route name was the only part
// that still disagreed.
//
// --- one treatment, both sides --------------------------------------------
//
// The broadcaster side used to alternate photographs left and right: the same
// deli window, morning to dusk. Good photographs, and the wrong job for them.
// A numbered list of three short steps is a thing you scan in five seconds,
// and putting a 400px image beside each one turned five seconds into a scroll
// -- while the advertiser side, which never had images, said the same amount
// in a third of the height.
//
// So both sides now use the same treatment, and the page is symmetrical: the
// tab switch changes the words, not the shape. That symmetry is worth more
// here than the photographs were, because the two sides are the same offer
// seen from opposite ends and the page should look like it.
//
// The photographs are not deleted, just unreferenced -- vendor-panel-{1,2,3}
// in src/images. Nothing imports them, so webpack does not emit them, and
// they are a copy-paste away from a section that actually wants a picture.

const STEPS = {
    broadcasters: [
        {
            title: "Sign up and pick your screen",
            body:
                "Join free. Choose a display from our recommended hardware, or tell " +
                "us about one you already own.",
        },
        {
            title: "Hang it and plug in the box",
            body:
                "Put it on a wall your customers face — the waiting area, the lobby, " +
                "above the counter. The WallBurst Box connects it to the network: it " +
                "needs power and your WiFi, nothing else.",
        },
        {
            title: "Set your price and get paid",
            body:
                "You set the daily rate and approve every ad before it plays. " +
                "Earnings clear after a campaign runs, and you withdraw them when " +
                "you want.",
        },
    ],
    // No bidding. The previous copy promised "compete for high-demand
    // locations", and there is no bid, auction or competitive allocation
    // anywhere in the system -- booking is first-come, enforced by a UNIQUE
    // constraint on the screen-day. A public promise the product cannot keep
    // is worse than a plainer one it can.
    advertisers: [
        {
            title: "Find a screen",
            body:
                "Browse by map or by neighbourhood. Every listing shows the kind of " +
                "business it is in, where the screen sits, its daily rate and the " +
                "hours still open — the price you see is the price you pay.",
        },
        {
            title: "Upload your ad and book the days",
            body:
                "Pick your dates and upload your artwork. The host reviews it before " +
                "anything goes live in their business, and you are only charged for " +
                "the hours you book.",
        },
        {
            title: "Go live where your audience is",
            body:
                "Once approved, your ad plays on a real screen in a real place — a " +
                "waiting room, a dining room, a lobby. Change the artwork " +
                "mid-campaign without rebooking, or cancel for a refund.",
        },
    ],
};

const TABS = [
    // "I have a space", not "I have a window" or "I have a screen". Neither of
    // the old labels described the person we most want on this tab: a business
    // with a wall and no display on it yet.
    { key: "broadcasters", label: "I have a space" },
    { key: "advertisers", label: "I want to advertise" },
];

function Steps({ steps }) {
    return (
        <ol className="hiw__steps">
            {steps.map((step) => (
                <li className="hiw__step" key={step.title}>
                    {/* The numeral is decorative: <ol> already conveys order to
                        a screen reader, and reading "1" twice per step is
                        noise. */}
                    <span className="hiw__numeral" aria-hidden="true" />
                    <div>
                        <h3>{step.title}</h3>
                        <p>{step.body}</p>
                    </div>
                </li>
            ))}
        </ol>
    );
}

export default function HowItWorks() {
    const [tab, setTab] = useState("broadcasters");
    const isBroadcaster = tab === "broadcasters";
    const tabRefs = useRef({});

    // Left/Right move between tabs, Home/End jump to the ends. Declaring
    // role="tablist" is a promise that this works -- a screen-reader user is
    // told these are tabs and then reaches for the arrow keys. Without it the
    // ARIA is a description of a control that does not exist.
    function onTabKey(e) {
        const i = TABS.findIndex((t) => t.key === tab);
        let next = null;
        if (e.key === "ArrowRight") next = TABS[(i + 1) % TABS.length];
        else if (e.key === "ArrowLeft") next = TABS[(i - 1 + TABS.length) % TABS.length];
        else if (e.key === "Home") next = TABS[0];
        else if (e.key === "End") next = TABS[TABS.length - 1];
        if (!next) return;
        e.preventDefault();
        setTab(next.key);
        const el = tabRefs.current[next.key];
        if (el) el.focus();
    }

    return (
        <div className="hiw">
            <header className="hiw__head">
                <h1 className="hiw__title">How it works</h1>
                <p className="hiw__lede">
                    WallBurst has two sides. One has a room full of people; the other
                    has something to tell them.
                </p>
            </header>

            {/* Tabs, not two stacked sections: a visitor is one of these two
                people and has no reason to read the other half. */}
            <div
                className="hiw__tabs"
                role="tablist"
                aria-label="Which side are you on?"
                onKeyDown={onTabKey}
            >
                {TABS.map((t) => {
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            id={"tab-" + t.key}
                            ref={(el) => { tabRefs.current[t.key] = el; }}
                            role="tab"
                            aria-selected={active}
                            aria-controls={"panel-" + t.key}
                            // Only the selected tab is in the tab order: Tab
                            // moves past the whole group to the panel, arrows
                            // move within it. That is the point of the pattern.
                            tabIndex={active ? 0 : -1}
                            className={"hiw__tab" + (active ? " is-active" : "")}
                            onClick={() => setTab(t.key)}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <section
                id={"panel-" + tab}
                role="tabpanel"
                aria-labelledby={"tab-" + tab}
                tabIndex={-1}
                className="hiw__panel"
            >
                <div className="hiw__intro">
                    <h2>
                        {isBroadcaster
                            ? "Got a wall? Put it to work."
                            : "Advertise where people actually are"}
                    </h2>
                    <p>
                        {isBroadcaster
                            ? "Your customers are already spending time here. Your walls might as well earn something too. WallBurst turns available wall space into a simple new revenue stream — bookable by the day, at a price you set, with every ad approved by you before it plays."
                            : "Salons, cafés, gyms, studios, waiting rooms — screens inside the places your neighbourhood already spends time in. Book one by the day, with no contract and nothing to print."}
                    </p>
                </div>

                <Steps steps={STEPS[tab]} />

                {isBroadcaster ? (
                    // Said here rather than discovered at the registration form,
                    // which is the worst possible moment to learn that signing
                    // up is not open to you.
                    <p className="hiw__note">
                        Hosting a screen is invitation-only while we deploy hardware a
                        few sites at a time. Joining the waitlist takes a minute.
                    </p>
                ) : (
                    // One worked example, on the tab where it does work. The
                    // advertiser side has to answer "why is a screen in a nail
                    // salon worth anything?", and the answer is a number: how
                    // long somebody sits in front of it. Reusing hiw__note
                    // rather than inventing a section for one paragraph.
                    <p className="hiw__note">
                        Why indoors? A nail salon customer is there for 30 to 90
                        minutes. Parents at a dance studio wait most of a lesson.
                        That is a long time next to a screen, and it is time
                        nobody spends in front of a billboard.
                    </p>
                )}

                <div className="hiw__cta">
                    <p>
                        {isBroadcaster
                            ? "No selling, no printing, no contracts. The space you already have does the work."
                            : "Fast, local and flexible — and you always see the real price before you book."}
                    </p>
                    <div className="hiw__cta-actions">
                        {isBroadcaster ? (
                            <>
                                <Link to="/waitlist" className="wb-btn wb-btn--primary wb-btn--lg">
                                    Become a host
                                </Link>
                                {/* The estimator is the softer second step: it
                                    asks for an address rather than a commitment.
                                    It is secondary rather than primary now --
                                    its model reads vehicle counts on the nearest
                                    road, which fitted a screen in a window far
                                    better than it fits one in a waiting room. */}
                                <Link to="/estimate" className="wb-btn wb-btn--secondary wb-btn--lg">
                                    What’s my wall worth?
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link to="/devices" className="wb-btn wb-btn--primary wb-btn--lg">
                                    Explore locations
                                </Link>
                                <Link
                                    to="/register/customer"
                                    className="wb-btn wb-btn--secondary wb-btn--lg"
                                >
                                    Create an account
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
